import { Fragment } from 'react';

// Every scene morphs one line from "without Avalio" (A) to "with Avalio" (B) where the slider has passed
export const N = 220;
export const X0 = 0.06;
export const X1 = 0.94;

const lerp = (a, b, t) => a + (b - a) * t;
const px = (v) => v.toFixed(1);
const fade = (v) => Math.min(1, v).toFixed(2);

const INK = [2, 35, 31];
const CREAM = [238, 239, 226];
const mixColor = (a, b, t) => `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(',')})`;

// Linear interpolation through [x, y] key points
const piece = (keys) => (x) => {
  if (x <= keys[0][0]) return keys[0][1];
  for (let i = 0; i < keys.length - 1; i++) {
    const [x0, y0] = keys[i];
    const [x1, y1] = keys[i + 1];
    if (x <= x1) return lerp(y0, y1, (x - x0) / (x1 - x0));
  }
  return keys[keys.length - 1][1];
};

const xs = Array.from({ length: N }, (_, i) => X0 + (i / (N - 1)) * (X1 - X0));
const byX = (fa, fb) => ({ A: xs.map((x) => [x, fa(x)]), B: xs.map((x) => [x, fb(x)]) });
const priceY = (value) => 1 - (value - 35) / (46 - 35);

// side 'b' sits on the light "before" pane, 'a' on the dark "after" pane, none follows the slider
const Txt = ({ h, x, y, dy = 0, side, anchor = 'middle', fill, size, weight = 500, opacity = 1, children }) => {
  if (opacity <= 0.02) return null;
  const color = fill || (side === 'b' ? '#02231F' : side === 'a' ? '#EEEFE2' : mixColor(INK, CREAM, h.tAt(x)));

  return (
    <text
      x={px(h.X(x))}
      y={px(h.Y(y) + dy)}
      textAnchor={anchor}
      fontSize={px(size || h.fs)}
      fontWeight={weight}
      fill={color}
      opacity={fade(opacity)}
    >
      {children}
    </text>
  );
};

const Dot = ({ h, p, opacity }) =>
  opacity <= 0.02 ? null : (
    <circle
      cx={px(h.X(p[0]))}
      cy={px(h.Y(p[1]))}
      r='4.5'
      fill='#EDE5D6'
      stroke='#02231F'
      strokeWidth='2'
      opacity={fade(opacity)}
    />
  );

const Diamond = ({ h, x, y, fill, opacity }) =>
  opacity <= 0.02 ? null : (
    <path
      d={`M${px(h.X(x))},${px(h.Y(y) - 7)} l7,7 l-7,7 l-7,-7z`}
      fill={fill}
      opacity={fade(opacity)}
    />
  );

const Zone = ({ h, x, y, x2, y2, fill, opacity }) =>
  opacity <= 0.02 ? null : (
    <rect
      x={px(h.X(x))}
      y={px(h.Y(y))}
      width={px(h.X(x2) - h.X(x))}
      height={px(h.Y(y2) - h.Y(y))}
      fill={fill}
      opacity={opacity.toFixed(2)}
    />
  );

// 01 Orders: five tangled channels straighten into one line
const orders = {
  A: xs.map((x, i) => {
    const s = i / (N - 1);
    return [x + 0.05 * Math.sin(s * Math.PI * 9), 0.5 + 0.28 * Math.sin(s * Math.PI * 3.2) + 0.14 * Math.cos(s * Math.PI * 9)];
  }),
  B: xs.map((x) => [x, 0.5]),
  decorate: (h, c) => {
    const avalio = h.tAt(0.2);
    const arrowX = h.edge - 0.015;
    return {
      over: (
        <>
          {[0.04, 0.27, 0.5, 0.73, 0.96].map((f, k) => {
            const i = Math.round(f * (N - 1));
            const q = h.at(i);
            const t = h.ti(i);
            return (
              <Fragment key={f}>
                <Dot
                  h={h}
                  p={q}
                  opacity={1 - t}
                />
                <Txt
                  h={h}
                  x={q[0]}
                  y={q[1]}
                  dy={-13}
                  side='b'
                  opacity={1 - t}
                >
                  {c.channels[k]}
                </Txt>
              </Fragment>
            );
          })}
          {avalio > 0.02 && (
            <>
              <circle
                cx={px(h.X(0.2))}
                cy={px(h.Y(0.5))}
                r={px(h.fs * 1.9 * avalio)}
                fill='#645BE8'
                opacity={avalio.toFixed(2)}
              />
              <Txt
                h={h}
                x={0.2}
                y={0.5}
                dy={h.fs * 0.35}
                side='a'
                fill='#fff'
                weight={600}
                opacity={avalio}
              >
                Avalio
              </Txt>
            </>
          )}
          <Txt
            h={h}
            x={0.24}
            y={0.5}
            dy={h.fs * 2.1}
            side='a'
            anchor='start'
            fill='#9BF0C9'
            opacity={h.tAt(0.46)}
          >
            {c.orderSent}
          </Txt>
          {arrowX > 0.34 && (
            <path
              d={`M${px(h.X(arrowX) - 10)},${px(h.Y(0.5) - 7)} L${px(h.X(arrowX))},${px(h.Y(0.5))} L${px(h.X(arrowX) - 10)},${px(h.Y(0.5) + 7)}`}
              fill='none'
              stroke='#9BF0C9'
              strokeWidth='3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          )}
        </>
      )
    };
  }
};

const PRICES = [
  { x: 0.16, value: 42.9 },
  { x: 0.37, value: 41.2 },
  { x: 0.59, value: 44.5 },
  { x: 0.82, value: 39.5 }
];

// 02 Prices: jagged prices of four suppliers level out at one lower price
const prices = {
  ...byX(
    (x) =>
      piece([
        [0.06, priceY(42.9)],
        [0.255, priceY(42.9)],
        [0.27, priceY(41.2)],
        [0.475, priceY(41.2)],
        [0.49, priceY(44.5)],
        [0.695, priceY(44.5)],
        [0.71, priceY(39.5)],
        [0.94, priceY(39.5)]
      ])(x) +
      0.022 * Math.sin(x * 97) +
      0.018 * Math.sin(x * 263 + 1.3) +
      0.012 * Math.sin(x * 541 + 0.4),
    () => priceY(36.4)
  ),
  decorate: (h, c) => ({
    over: (
      <>
        {PRICES.map(({ x, value }, k) => (
          <Fragment key={x}>
            <Txt
              h={h}
              x={x}
              y={priceY(value)}
              dy={-18}
              side='b'
              weight={600}
              opacity={1 - h.tAt(x)}
            >
              {c.prices[k]}
            </Txt>
            <Txt
              h={h}
              x={x}
              y={1.12}
              size={h.fs * 0.85}
              opacity={0.75}
            >
              {c.suppliers[k]}
            </Txt>
          </Fragment>
        ))}
        <Txt
          h={h}
          x={X0}
          y={priceY(36.4)}
          dy={-16}
          side='a'
          anchor='start'
          fill='#9BF0C9'
          weight={600}
          opacity={h.tAt(0.2)}
        >
          {c.priceAfter}
        </Txt>
        <Txt
          h={h}
          x={X0}
          y={priceY(36.4)}
          dy={h.fs * 1.9}
          side='a'
          anchor='start'
          fill='#AAA4FF'
          opacity={h.tAt(0.25)}
        >
          {c.priceList}
        </Txt>
      </>
    )
  })
};

// 03 Inventory: stock dropping to zero becomes a saw that stays above the minimum
const stock = {
  ...byX(
    piece([
      [0.06, 0.05],
      [0.24, 0.05],
      [0.25, 0.25],
      [0.42, 0.25],
      [0.43, 0.45],
      [0.58, 0.45],
      [0.59, 0.7],
      [0.7, 0.7],
      [0.71, 0.98],
      [0.94, 0.98]
    ]),
    piece([
      [0.06, 0.05],
      [0.29, 0.55],
      [0.3, 0.05],
      [0.58, 0.55],
      [0.59, 0.05],
      [0.94, 0.48]
    ])
  ),
  decorate: (h, c) => {
    const shortage = 1 - h.tAt(0.82);
    return {
      under: (
        <>
          <path
            d={`M${px(h.X(X0))},${px(h.Y(0.98))} H${px(h.X(X1))}`}
            stroke='rgba(120,124,110,.35)'
            fill='none'
          />
          <path
            d={`M${px(h.X(X0))},${px(h.Y(0.62))} H${px(h.X(X1))}`}
            stroke='#E08A3C'
            strokeWidth='1.5'
            strokeDasharray='4 5'
            fill='none'
          />
          <Zone
            h={h}
            x={0.71}
            y={0.62}
            x2={0.94}
            y2={0.98}
            fill='rgba(194,83,28,.16)'
            opacity={shortage}
          />
        </>
      ),
      over: (
        <>
          <Txt
            h={h}
            x={X0}
            y={0.62}
            dy={-7}
            anchor='start'
            fill='#D9772F'
            size={h.fs * 0.9}
          >
            min.
          </Txt>
          <Txt
            h={h}
            x={0.825}
            y={0.62}
            dy={h.fs * 1.6}
            side='b'
            fill='#A8431A'
            size={h.fs * 0.9}
            opacity={shortage}
          >
            {c.patient}
          </Txt>
          <Txt
            h={h}
            x={0.825}
            y={0.98}
            dy={-9}
            side='b'
            fill='#A8431A'
            weight={700}
            opacity={shortage}
          >
            {c.out}
          </Txt>
          {[0.3, 0.59].map((x) => (
            <Txt
              key={x}
              h={h}
              x={x}
              y={0.05}
              dy={-12}
              side='a'
              fill='#9BF0C9'
              opacity={h.tAt(x)}
            >
              {c.delivery}
            </Txt>
          ))}
          {c.days.map((day, k) => (
            <Txt
              key={k}
              h={h}
              x={[0.15, 0.33, 0.51, 0.69, 0.87][k]}
              y={1.13}
              size={h.fs * 0.85}
              opacity={0.7}
            >
              {day}
            </Txt>
          ))}
        </>
      )
    };
  }
};

// 04 Equipment: a long outage shrinks to a short dip, inspections get scheduled
const equipment = {
  ...byX(
    piece([
      [0.06, 0.1],
      [0.2, 0.1],
      [0.215, 0.85],
      [0.82, 0.85],
      [0.835, 0.1],
      [0.94, 0.1]
    ]),
    piece([
      [0.06, 0.1],
      [0.2, 0.1],
      [0.215, 0.85],
      [0.29, 0.85],
      [0.305, 0.1],
      [0.94, 0.1]
    ])
  ),
  decorate: (h, c) => {
    const outage = 1 - h.tAt(0.52);
    const unplanned = 1 - h.tAt(0.14);
    return {
      under: (
        <Zone
          h={h}
          x={0.215}
          y={0.1}
          x2={0.82}
          y2={0.85}
          fill='rgba(194,83,28,.12)'
          opacity={outage}
        />
      ),
      over: (
        <>
          <Txt
            h={h}
            x={X0}
            y={0.1}
            dy={-12}
            anchor='start'
            size={h.fs * 0.9}
          >
            {c.working}
          </Txt>
          <Txt
            h={h}
            x={X0}
            y={0.85}
            dy={h.fs * 0.4}
            anchor='start'
            size={h.fs * 0.9}
          >
            {c.down}
          </Txt>
          <Diamond
            h={h}
            x={0.14}
            y={0.1}
            fill='#C2531C'
            opacity={unplanned}
          />
          <Txt
            h={h}
            x={0.14}
            y={0.1}
            dy={26}
            side='b'
            fill='#A8431A'
            size={h.fs * 0.9}
            opacity={unplanned}
          >
            {c.inspectionQuestion}
          </Txt>
          <Txt
            h={h}
            x={0.52}
            y={0.85}
            dy={-48}
            side='b'
            fill='#A8431A'
            weight={700}
            opacity={outage}
          >
            {c.downtime}
          </Txt>
          <Txt
            h={h}
            x={0.52}
            y={0.85}
            dy={-24}
            side='b'
            size={h.fs * 0.9}
            opacity={outage}
          >
            {c.flow.join(' → ')}
          </Txt>
          <Txt
            h={h}
            x={0.255}
            y={0.85}
            dy={h.fs * 1.7}
            side='a'
            fill='#9BF0C9'
            opacity={h.tAt(0.255)}
          >
            {c.byVoice}
          </Txt>
          {[0.55, 0.8].map((x) => (
            <Fragment key={x}>
              <Diamond
                h={h}
                x={x}
                y={0.1}
                fill='#AAA4FF'
                opacity={h.tAt(x)}
              />
              <Txt
                h={h}
                x={x}
                y={0.1}
                dy={28}
                side='a'
                fill='#AAA4FF'
                opacity={h.tAt(x)}
              >
                {c.inspection}
              </Txt>
            </Fragment>
          ))}
        </>
      )
    };
  }
};

export const SCENES = [orders, prices, stock, equipment];
